import {
  Row, Col, message, Table, Tag, Button, Tooltip, Typography, Modal, Spin, Alert, Space, Divider, Popover,
} from "antd";
import { ColumnsType } from "antd/lib/table";
import { AppPageHeader } from "components/AppPageHeader";
import { AppActionStrip } from "components/AppActionStrip/AppActionStrip";
import { AppFilter, AppFilterButton, AppDispatchInvoiceFilter } from "components/AppFilter";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { useDocumentTitle } from "hooks/usePageTitle";
import { useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";
import { IDispatchInvoice, IDispatchInvoiceGenerateResult } from "common/interface/IDispatchInvoice";
import {
  getDispatchInvoices,
  generateDispatchInvoice,
  clearGenerateResult,
} from "store/dispatch-invoice/dispatchInvoiceSlice";
import { DownloadOutlined, ExclamationCircleFilled, FileOutlined } from "@ant-design/icons";
import { useSearchParams } from "react-router-dom";
import styled from "styled-components";

const MESSAGE_KEY = "dispatch_invoices";

const dispatchStatusColor: Record<string, string> = {
  DISPATCHED: "green",
  PENDING: "orange",
  CANCELLED: "red",
};

const einvoiceStatusColor: Record<string, string> = {
  GENERATED: "green",
  SUCCESS: "green",
  FAILED: "red",
  PENDING: "orange",
};

const TruncatedText = styled(Typography.Text)`
  max-width: 180px;
  display: block;
`;

const DocumentCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  background: #fafafa;

  &:not(:last-child) {
    margin-bottom: 10px;
  }
`;

const DocInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

type RowType = IDispatchInvoice & { key: string };

interface DownloadDoc {
  label: string;
  urlKey: keyof IDispatchInvoiceGenerateResult;
  statusKey: keyof IDispatchInvoiceGenerateResult | null;
}

const DOWNLOAD_DOCS: DownloadDoc[] = [
  { label: "E-Invoice PDF",    urlKey: "einvoicePdfUrl",    statusKey: "einvoiceStatus" },
  { label: "EWB PDF",          urlKey: "ewbPdfUrl",         statusKey: "einvoiceStatus" },
  { label: "BCFD Challan",     urlKey: "bcfdChallanUrl",    statusKey: "bcfdChallanStatus" },
  { label: "AN Farm Challan",  urlKey: "anFarmChallanUrl",  statusKey: "anFarmChallanStatus" },
];

const openDownload = (url: string) => {
  window.open(url, "_blank", "noopener,noreferrer");
};

const DispatchInvoicePage = () => {
  useDocumentTitle("Dispatch Invoice");
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const [filterState, setFilterState] = useState(false);
  const [filterCount, setFilterCount] = useState(0);
  const [modalDispatch, setModalDispatch] = useState<RowType | null>(null);

  const { invoices, status, error, generateResult, generateStatus, generateError } =
    useAppSelector((state) => state.dispatchInvoice);

  const fetchData = useCallback(
    (filter: string) => {
      const params = Object.fromEntries(new URLSearchParams(filter));
      const date = params["date"] || moment().add(1, "day").format("YYYY-MM-DD");
      dispatch(getDispatchInvoices({ date }));
    },
    [dispatch]
  );

  useEffect(() => {
    const filter = searchParams.toString();
    // count only search filters, not the date param which is always present
    setFilterCount(["dispatchId", "vehicleNo"].filter((k) => searchParams.get(k)).length);
    fetchData(filter);
  }, [fetchData, searchParams]);

  useEffect(() => {
    if (status === "loading") {
      message.loading({ content: "Loading dispatch invoices…", key: MESSAGE_KEY });
    } else if (status === "error") {
      message.error({ content: error ?? "Failed to load dispatch invoices", key: MESSAGE_KEY });
    } else {
      message.destroy(MESSAGE_KEY);
    }
  }, [status, error]);

  const openModal = (record: RowType) => {
    setModalDispatch(record);
    dispatch(generateDispatchInvoice({ dispatchId: record.dispatchId }));
  };

  const closeModal = () => {
    setModalDispatch(null);
    dispatch(clearGenerateResult());
  };

  // Current date being displayed (from params or today)
  const activeDate =
    searchParams.get("date") || moment().add(1, "day").format("YYYY-MM-DD");

  const columns: ColumnsType<RowType> = [
    {
      key: "dispatchId",
      title: "Dispatch ID",
      dataIndex: "dispatchId",
      width: 110,
      fixed: "left",
    },
    {
      key: "storeId",
      title: "Store ID",
      dataIndex: "storeId",
      width: 90,
    },
    {
      key: "vehicleNo",
      title: "Vehicle No",
      dataIndex: "vehicleNo",
      width: 110,
      render: (val: string | null) => val ?? "—",
    },
    {
      key: "dispatchStatus",
      title: "Dispatch Status",
      dataIndex: "dispatchStatus",
      width: 140,
      render: (val: string | null) =>
        val ? <Tag color={dispatchStatusColor[val] ?? "default"}>{val}</Tag> : "—",
    },
    {
      key: "einvoiceStatus",
      title: "E-Invoice Status",
      dataIndex: "einvoiceStatus",
      width: 150,
      render: (val: string | null) =>
        val ? (
          <Tag color={einvoiceStatusColor[val] ?? "default"}>{val}</Tag>
        ) : (
          <Tag color="default">—</Tag>
        ),
    },
    {
      key: "irn",
      title: "IRN",
      dataIndex: "irn",
      width: 200,
      render: (val: string | null) =>
        val ? (
          <Tooltip title={val}>
            <TruncatedText ellipsis>{val}</TruncatedText>
          </Tooltip>
        ) : (
          "—"
        ),
    },
    {
      key: "ewbNo",
      title: "EWB No",
      dataIndex: "ewbNo",
      width: 140,
      render: (val: string | null) => val ?? "—",
    },
    {
      key: "errorMessage",
      title: "Error",
      dataIndex: "errorMessage",
      width: 60,
      align: "center",
      render: (val: string | null) =>
        val ? (
          <Popover
            content={
              <Typography.Text style={{ maxWidth: 320, display: "block" }}>
                {val}
              </Typography.Text>
            }
            title="Error Details"
            trigger="click"
            overlayStyle={{ maxWidth: 340 }}
          >
            <ExclamationCircleFilled
              style={{ color: "#ff4d4f", fontSize: 16, cursor: "pointer" }}
            />
          </Popover>
        ) : (
          "—"
        ),
    },
    {
      key: "actions",
      title: "Actions",
      width: 80,
      fixed: "right",
      align: "center",
      render: (_: unknown, record: RowType) => (
        <Tooltip title="Download Documents">
          <Button
            size="small"
            type="primary"
            shape="circle"
            icon={<DownloadOutlined />}
            onClick={() => openModal(record)}
          />
        </Tooltip>
      ),
    },
  ];

  const rows: RowType[] = useMemo(() => {
    const dispatchIdQuery = searchParams.get("dispatchId")?.trim() ?? "";
    const vehicleNoQuery = searchParams.get("vehicleNo")?.trim().toLowerCase() ?? "";
    return invoices
      .filter((inv) => {
        const matchesDispatchId = !dispatchIdQuery || String(inv.dispatchId).includes(dispatchIdQuery);
        const matchesVehicleNo = !vehicleNoQuery || inv.vehicleNo?.toLowerCase().includes(vehicleNoQuery);
        return matchesDispatchId && matchesVehicleNo;
      })
      .map((inv, i) => ({ ...inv, key: `inv-${i}` }));
  }, [invoices, searchParams]);

  return (
    <>
      <Row justify="space-between" align="middle">
        <Col>
          <AppPageHeader title="Dispatch Invoice" />
        </Col>
      </Row>

      <Row>
        <AppActionStrip>
          <div className="action-strip__item">
            <AppFilterButton
              active={filterState}
              onClick={() => setFilterState((s) => !s)}
              filterCount={filterCount}
            />
          </div>
        </AppActionStrip>
      </Row>

      <Row style={{ marginBottom: "1rem" }}>
        <AppFilter expanded={filterState}>
          <AppDispatchInvoiceFilter />
        </AppFilter>
      </Row>

      {rows.length > 0 ? (
        <Row>
          <Col xs={24}>
            <Table
              columns={columns}
              dataSource={rows}
              scroll={{ x: "max-content", y: "65vh" }}
              size="small"
              pagination={false}
              loading={status === "loading"}
              bordered
            />
          </Col>
        </Row>
      ) : (
        status !== "loading" && (
          <Row style={{ marginTop: "1rem" }}>
            <Col>No dispatch invoices found for {activeDate}.</Col>
          </Row>
        )
      )}

      {/* Download Documents Modal */}
      <Modal
        title={
          <Space>
            <DownloadOutlined />
            <span>
              Download Documents
              {modalDispatch ? ` — Dispatch #${modalDispatch.dispatchId}` : ""}
            </span>
          </Space>
        }
        open={modalDispatch !== null}
        onCancel={closeModal}
        footer={<Button onClick={closeModal}>Close</Button>}
        width={520}
        destroyOnClose
      >
        {generateStatus === "loading" && (
          <Row justify="center" style={{ padding: "2rem 0" }}>
            <Spin tip="Generating documents…" size="large" />
          </Row>
        )}

        {generateStatus === "error" && (
          <Alert
            type="error"
            message="Failed to generate documents"
            description={generateError ?? "An unexpected error occurred. Please try again."}
            showIcon
          />
        )}

        {generateStatus === "success" && generateResult && (
          <>
            {generateResult.errorMessage && (
              <Alert
                type="warning"
                message={generateResult.errorMessage}
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}

            <Space size="large" style={{ marginBottom: 4 }}>
              {generateResult.irn && (
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  IRN:{" "}
                  <Tooltip title={generateResult.irn}>
                    <Typography.Text
                      ellipsis
                      style={{ maxWidth: 160, display: "inline-block", verticalAlign: "bottom" }}
                    >
                      {generateResult.irn}
                    </Typography.Text>
                  </Tooltip>
                </Typography.Text>
              )}
              {generateResult.ewbNo && (
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  EWB No: <strong>{generateResult.ewbNo}</strong>
                </Typography.Text>
              )}
            </Space>

            <Divider style={{ margin: "12px 0" }} />

            {DOWNLOAD_DOCS.map((doc) => {
              const url = generateResult[doc.urlKey] as string | null;
              const docStatus = doc.statusKey
                ? (generateResult[doc.statusKey] as string | null)
                : null;

              return (
                <DocumentCard key={doc.urlKey}>
                  <DocInfo>
                    <Space align="center">
                      <FileOutlined style={{ color: "#1890ff" }} />
                      <Typography.Text strong>{doc.label}</Typography.Text>
                    </Space>
                    {docStatus && (
                      <Tag
                        color={einvoiceStatusColor[docStatus] ?? "default"}
                        style={{ marginTop: 4, alignSelf: "flex-start" }}
                      >
                        {docStatus}
                      </Tag>
                    )}
                  </DocInfo>
                  <Button
                    type={url ? "primary" : "default"}
                    icon={<DownloadOutlined />}
                    size="small"
                    disabled={!url}
                    onClick={() => url && openDownload(url)}
                  >
                    Download
                  </Button>
                </DocumentCard>
              );
            })}
          </>
        )}
      </Modal>
    </>
  );
};

export default DispatchInvoicePage;
